import { useSuspenseQuery } from '@apollo/client';
import type React from 'react';
import { useEffect, useRef, useState, useMemo, Suspense } from 'react';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';

import { useArticleEditor } from '@xing-com/crate-articles-article-editor';
import NotFound from '@xing-com/crate-articles-not-found';
import {
  useTracking,
  type OperationalTrackingEvent,
  type NWTEvent,
} from '@xing-com/crate-communication-tracking';
import { useParams } from '@xing-com/crate-core-router';
import { useIsTopBarHidden } from '@xing-com/crate-hooks-use-is-top-bar-hidden';
import { TopBar } from '@xing-com/crate-layout-top-bar';
import { useFeature, useViewerData } from '@xing-com/hub';

import ArticleAuthorInfoBox from './components/article-author-info-box';
import ArticleAuthorProfile from './components/article-author-profile';
import ArticleCover from './components/article-cover';
import ArticleEditHeader from './components/article-edit-header';
import ArticleHelmet from './components/article-helmet';
import ArticleSocialInteractions from './components/article-social-interactions';
import ArticleViewerContent from './components/article-viewer-content';
import ErrorStatusBanner from './components/error-banner';
import LoadingState from './components/loading-state';
import {
  GetArticleByIdDocument,
  type GetArticleByIdQuery,
  type GetArticleByIdQueryVariables,
} from './graphql/get-article-query.gql-types';
import {
  validateShowPaywall,
  parseArticle,
  parseAuthor,
  unscrambleId,
  generateArticleTrackingTypeString,
  getNwtInsiderUrn,
} from './helpers';
import { useUserLoginState } from './hooks/user-login-state';
import { Styled } from './styles';
import { type ArticleViewerProps } from './types';

type PageViewTrackingType = {
  PropContextDimension1?: string;
  PropContextDimension2?: string;
  EventNewsArticleView?: number;
  PropNewsArticleId?: string;
  PropNewsProduct?: string;
  PropNewsPublisherId?: string;
  PropLoginState?: string;
};

type SocialScrollingTrackingType = {
  PropScrolling: string;
};

const ArticleViewerInner: React.FC<ArticleViewerProps> = ({
  article: articleProp,
}) => {
  const isPageLoaded = useRef(false);

  const userIsPremium = useFeature('isPremium');
  const userHasProJobsMembership = useFeature('hasProJobsMembership');

  const { isLoggedOut } = useUserLoginState();
  const { track: nwTracking } = useTracking<NWTEvent>();
  const { track: pageviewTracking } = useTracking<PageViewTrackingType>();
  const { track: odtTracking } = useTracking<OperationalTrackingEvent>();
  const { track: socialScrollingTracking } =
    useTracking<SocialScrollingTrackingType>();

  const intl = useIntl();
  const { id: articleSlug } = useParams();
  const isTopBarHidden = useIsTopBarHidden();

  const { article: contextArticle, updateArticle } = useArticleEditor();
  const viewerData = useViewerData().data;
  const [isFollowingAuthor, setIsFollowingAuthor] = useState<boolean>(false);

  const [showErrorBanner, setShowErrorBanner] = useState<boolean>(false);

  const skipArticleQuery = !articleSlug || !!articleProp;

  const { data, error } = useSuspenseQuery<
    GetArticleByIdQuery,
    GetArticleByIdQueryVariables
  >(GetArticleByIdDocument, {
    errorPolicy: 'all',
    skip: skipArticleQuery,
    variables: {
      articleID: articleSlug,
    },
  });

  const isInsiderPage = useMemo(
    () => data?.articlesArticle?.context?.__typename === 'ContentInsiderPage',
    [data]
  );

  const getArticleDetailPage = (isInsider: boolean): string =>
    `content/${isInsider ? 'insider' : 'newspage'}/article_detailpage`;
  const article = data ? parseArticle(data) : contextArticle;
  const author = data ? parseAuthor(data) : null;
  const canEdit = data?.articlesArticle?.context?.abilities.canEdit ?? false;
  const showPaywall =
    !canEdit &&
    validateShowPaywall(
      article.settings?.membership,
      userIsPremium ?? false,
      userHasProJobsMembership ?? false
    );

  useEffect(() => {
    if (articleProp) {
      updateArticle(articleProp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleProp]);

  useEffect(() => {
    if (!isPageLoaded.current) {
      const articleQueryData = data?.articlesArticle;

      if (articleQueryData) {
        const articleData = parseArticle(data);
        updateArticle(articleData);
        setIsFollowingAuthor(
          data.articlesArticle?.context?.interactions?.isFollowed ?? false
        );

        const pageType = isInsiderPage ? 'insider_pages' : 'publisher_pages';

        const channel = isInsiderPage
          ? 'wbm/News_Insider'
          : 'wbm/News_NewsPage';

        const contextId = data.articlesArticle?.context?.id;
        const contextGlobalId = data.articlesArticle?.contextGlobalId;
        const itemActorUrn = isInsiderPage
          ? getNwtInsiderUrn(contextGlobalId, contextId)
          : contextGlobalId;

        const author = parseAuthor(data);
        pageviewTracking({
          type: 'pageview',
          channel,
          page: `${channel}/article_detailpage`,
          PropContextDimension1: `news_${pageType}_FulltextArticleView`,
          PropContextDimension2: `news_${pageType}_article_editor_version3`,
          EventNewsArticleView: 1,
          PropNewsArticleId: unscrambleId(data.articlesArticle?.id ?? ''),
          PropNewsPublisherId: unscrambleId(author.id ?? ''),
          PropNewsProduct: pageType,
          PropLoginState: isLoggedOut ? 'logged_out' : undefined,
        });
        odtTracking({
          type: 'operational',
          trackingTokens: [],
          sentBy: 'content',
          login: viewerData?.user?.id
            ? {
                userId: viewerData?.user?.id,
              }
            : undefined, // login should be null when user is logged out.
          event: 'viewed',
          objectUrn: articleQueryData.globalId,
          objectActorUrn: articleQueryData.contextGlobalId,
          context: {
            page: 'News_Insider/article_detailpage',
          },
          additionalInfo: {},
        });
        const pageViewSharedTrackingOptions = {
          type: 'nwt' as const,
          context: generateArticleTrackingTypeString(articleData, showPaywall),
          application: 'wbm',
          page: getArticleDetailPage(isInsiderPage),
          sentBy: 'content',
          itemUrn: articleData.globalId,
        };

        nwTracking({
          ...pageViewSharedTrackingOptions,
          event: 'viewed_screen',
          eventSchema: 'basic',
        });
        nwTracking({
          ...pageViewSharedTrackingOptions,
          event: 'article_viewed',
          eventSchema: 'extended',
          itemActorUrn,
        });
      } else {
        pageviewTracking({
          type: 'pageview',
          channel: 'wbm/News_Insider',
          page: 'wbm/News_Insider/article_detailpage',
        });
      }

      isPageLoaded.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { ref: inViewRef } = useInView({
    onChange: (inView) => {
      if (inView) handleSocialVisible();
    },
    triggerOnce: true,
    delay: 500,
  });

  if (error) {
    return (
      <NotFound
        copy={intl.formatMessage({
          id: 'ARTICLES_ARTICLE_VIEWER_NOT_FOUND_MESSAGE',
        })}
      />
    );
  }

  if (!skipArticleQuery && !data?.articlesArticle) {
    return (
      <NotFound
        copy={intl.formatMessage({
          id: 'ARTICLES_ARTICLE_VIEWER_NOT_FOUND_MESSAGE',
        })}
      />
    );
  }

  const handleSocialVisible = (): void => {
    socialScrollingTracking({
      type: 'interaction',
      event: 'PropScrolling',
      PropScrolling:
        'news_articledetailpage_bottom_end_of_article_fta_scroll_yes',
    });
  };

  const handleMutationError = (): void => {
    setShowErrorBanner(true);
  };

  const handleFollowingClick = (newIsFollowingState: boolean): void => {
    setIsFollowingAuthor(newIsFollowingState);
    const followingState = newIsFollowingState ? 'followed' : 'unfollowed';
    nwTracking({
      type: 'nwt',
      eventSchema: 'basic',
      application: 'wbm',
      sentBy: 'content',
      page: getArticleDetailPage(isInsiderPage),
      itemUrn: article.globalId,
      event: followingState,
    });
  };

  const page = isInsiderPage ? 'news_insider' : 'news_newspage';

  return (
    <>
      <ArticleHelmet article={article} skip={skipArticleQuery} />

      {!isLoggedOut && !isTopBarHidden && (
        <TopBar
          title={intl.formatMessage({
            id: 'ARTICLES_ARTICLE_VIEWER_TOP_BAR_TITLE',
          })}
          backButtonUrlAriaLabelSection={intl.formatMessage({
            id: 'ARTICLES_ARTICLE_VIEWER_TOP_BAR_TITLE',
          })}
          type="subheader"
          data-qa="article-viewer-topbar"
          skipAdditionalButtons
        />
      )}

      <Styled.PreviewContainer>
        <ArticleEditHeader
          show={canEdit}
          article={article}
          author={author}
          onError={handleMutationError}
        />

        <ArticleCover article={article} />

        <ArticleAuthorProfile
          author={author}
          publishedAt={article.publishedAt ?? undefined}
          isFollowingAuthor={isFollowingAuthor ?? false}
          onFollowingClick={handleFollowingClick}
        />

        <ArticleViewerContent
          article={article}
          author={author}
          showPaywall={showPaywall}
        />

        {!showPaywall && (
          <div ref={inViewRef}>
            <ArticleSocialInteractions
              data-qa="article-viewer-social-interactions"
              article={article}
              page={page}
            />
          </div>
        )}

        <ArticleAuthorProfile
          author={author}
          isFollowingAuthor={isFollowingAuthor ?? false}
          onFollowingClick={handleFollowingClick}
          position="footer"
        />

        <ArticleAuthorInfoBox articleId={article.id} author={author} />
      </Styled.PreviewContainer>

      <ErrorStatusBanner
        show={showErrorBanner}
        handleOnClose={() => setShowErrorBanner(false)}
      />
    </>
  );
};

const ArticleViewer: React.FC<ArticleViewerProps> = (props) => {
  return (
    <Suspense fallback={<LoadingState />}>
      <ArticleViewerInner {...props} />
    </Suspense>
  );
};

export default ArticleViewer;
